import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import Layout from "../../components/Layout/Layout";
import Loading from "../../components/Loading";
import {
  getNonWovenStockSummary,
  addNonWovenWastageApi,
  updateNonWovenWastageRecordApi,
  deleteNonWovenWastageApi,
  addItemWastageApi,
  updateItemWastageApi,
  deleteItemWastageApi,
} from "../../utils/api";
import { formatDate } from "../../utils/formatData";
import useStore from "../../utils/store";

const NonWovenStockPage = () => {
  const startDate = useStore((state) => state.startDate);
  const endDate = useStore((state) => state.endDate);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  // Active Tab: "purchase" | "sale" | "wastage" | "items"
  const [activeTab, setActiveTab] = useState("purchase");

  // Wastage Modal & Form State
  const [showWastageModal, setShowWastageModal] = useState(false);
  const [editingWastageId, setEditingWastageId] = useState(null);
  const [wastageForm, setWastageForm] = useState({
    date: new Date().toISOString().split("T")[0],
    quantity: "",
    price: "",
    description: "",
  });
  const [submittingWastage, setSubmittingWastage] = useState(false);

  // Item Wastage Modal & Form State
  const [showItemWastageModal, setShowItemWastageModal] = useState(false);
  const [editingItemWastageId, setEditingItemWastageId] = useState(null);
  const [itemWastageForm, setItemWastageForm] = useState({
    date: new Date().toISOString().split("T")[0],
    productName: "",
    quantity: "",
    price: "",
    description: "",
  });
  const [submittingItemWastage, setSubmittingItemWastage] = useState(false);

  const fetchStockData = useCallback(async () => {
    setLoading(true);
    try {
      const [success, res] = await getNonWovenStockSummary(startDate, endDate);
      if (success && res) {
        setData(res);
      } else {
        toast.error("Failed to load stock summary");
      }
    } catch (err) {
      console.error("Error fetching stock data:", err);
      toast.error("Error connecting to server");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchStockData();
  }, [fetchStockData]);

  // Open modal for Adding
  const handleOpenAddWastage = () => {
    setEditingWastageId(null);
    setWastageForm({
      date: new Date().toISOString().split("T")[0],
      quantity: "",
      price: "",
      description: "",
    });
    setShowWastageModal(true);
  };

  // Open modal for Editing
  const handleOpenEditWastage = (item) => {
    setEditingWastageId(item._id);
    let itemDate = new Date().toISOString().split("T")[0];
    if (item.date) {
      try {
        itemDate = new Date(item.date).toISOString().split("T")[0];
      } catch {
        itemDate = new Date().toISOString().split("T")[0];
      }
    }
    setWastageForm({
      date: itemDate,
      quantity: String(item.quantity ?? ""),
      price: String(item.price ?? ""),
      description: item.description || "",
    });
    setShowWastageModal(true);
  };

  // Submit Add / Edit Wastage
  const handleSubmitWastage = async (e) => {
    if (e) e.preventDefault();
    const qty = parseFloat(wastageForm.quantity);
    if (isNaN(qty) || qty <= 0) {
      return toast.error("Please enter a valid wastage quantity greater than 0");
    }

    const payload = {
      date: wastageForm.date || new Date().toISOString(),
      quantity: qty,
      price: parseFloat(wastageForm.price) || 0,
      description: wastageForm.description || "",
    };

    setSubmittingWastage(true);
    try {
      if (editingWastageId) {
        const [success, res] = await updateNonWovenWastageRecordApi(
          editingWastageId,
          payload
        );
        if (success) {
          toast.success("✅ Wastage entry updated successfully");
          setShowWastageModal(false);
          fetchStockData();
        } else {
          toast.error(res || "Failed to update wastage entry");
        }
      } else {
        const [success, res] = await addNonWovenWastageApi(payload);
        if (success) {
          toast.success("✅ Wastage entry added successfully");
          setShowWastageModal(false);
          fetchStockData();
        } else {
          toast.error(res || "Failed to add wastage entry");
        }
      }
    } catch (err) {
      console.error("Error saving wastage:", err);
      toast.error("Error saving wastage entry");
    } finally {
      setSubmittingWastage(false);
    }
  };

  // Delete Wastage
  const handleDeleteWastage = async (id) => {
    if (!window.confirm("Are you sure you want to delete this wastage record?")) {
      return;
    }
    try {
      const [success, res] = await deleteNonWovenWastageApi(id);
      if (success) {
        toast.success("🗑️ Wastage entry deleted");
        fetchStockData();
      } else {
        toast.error(res || "Failed to delete wastage entry");
      }
    } catch (err) {
      console.error("Error deleting wastage:", err);
      toast.error("Error deleting wastage entry");
    }
  };

  // Open modal for Adding Item Wastage
  const handleOpenAddItemWastage = () => {
    setEditingItemWastageId(null);
    setItemWastageForm({
      date: new Date().toISOString().split("T")[0],
      productName: "",
      quantity: "",
      price: "",
      description: "",
    });
    setShowItemWastageModal(true);
  };

  // Open modal for Adding Item Wastage with prefilled product
  const handleOpenAddItemWastageForProduct = (productName) => {
    setEditingItemWastageId(null);
    setItemWastageForm({
      date: new Date().toISOString().split("T")[0],
      productName: productName || "",
      quantity: "",
      price: "",
      description: "",
    });
    setShowItemWastageModal(true);
  };

  // Open modal for Editing Item Wastage
  const handleOpenEditItemWastage = (item) => {
    setEditingItemWastageId(item._id);
    let itemDate = new Date().toISOString().split("T")[0];
    if (item.date) {
      try {
        itemDate = new Date(item.date).toISOString().split("T")[0];
      } catch {
        itemDate = new Date().toISOString().split("T")[0];
      }
    }
    setItemWastageForm({
      date: itemDate,
      productName: item.productName || "",
      quantity: String(item.quantity ?? ""),
      price: String(item.price ?? ""),
      description: item.description || "",
    });
    setShowItemWastageModal(true);
  };

  // Submit Add / Edit Item Wastage
  const handleSubmitItemWastage = async (e) => {
    if (e) e.preventDefault();
    const qty = parseFloat(itemWastageForm.quantity);
    if (isNaN(qty) || qty <= 0) {
      return toast.error("Please enter a valid wastage quantity greater than 0");
    }
    if (!itemWastageForm.productName || !itemWastageForm.productName.trim()) {
      return toast.error("Please select or enter an item / product name");
    }

    const payload = {
      date: itemWastageForm.date || new Date().toISOString(),
      productName: itemWastageForm.productName.trim(),
      quantity: qty,
      price: parseFloat(itemWastageForm.price) || 0,
      description: itemWastageForm.description || "",
    };

    setSubmittingItemWastage(true);
    try {
      if (editingItemWastageId) {
        const [success, res] = await updateItemWastageApi(
          editingItemWastageId,
          payload
        );
        if (success) {
          toast.success("✅ Item wastage updated successfully");
          setShowItemWastageModal(false);
          fetchStockData();
        } else {
          toast.error(res || "Failed to update item wastage");
        }
      } else {
        const [success, res] = await addItemWastageApi(payload);
        if (success) {
          toast.success("✅ Item wastage added successfully");
          setShowItemWastageModal(false);
          fetchStockData();
        } else {
          toast.error(res || "Failed to add item wastage");
        }
      }
    } catch (err) {
      console.error("Error saving item wastage:", err);
      toast.error("Error saving item wastage");
    } finally {
      setSubmittingItemWastage(false);
    }
  };

  // Delete Item Wastage
  const handleDeleteItemWastage = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item wastage record?")) {
      return;
    }
    try {
      const [success, res] = await deleteItemWastageApi(id);
      if (success) {
        toast.success("🗑️ Item wastage deleted");
        fetchStockData();
      } else {
        toast.error(res || "Failed to delete item wastage");
      }
    } catch (err) {
      console.error("Error deleting item wastage:", err);
      toast.error("Error deleting item wastage");
    }
  };

  const purchaseQty = Number(data?.purchaseQty || 0);
  const saleQty = Number(data?.saleQty || 0);
  const wastageQty = Number(data?.wastageQty || 0);
  const balanceQty = Number(data?.balanceQty || 0);

  const purchaseList = data?.purchaseList || [];
  const saleList = data?.saleOrders || data?.tagSaleList || data?.saleList || [];
  const wastageList = data?.wastageList || [];
  const itemsStockList = data?.itemsStockList || [];
  const itemWastageList = data?.itemWastageList || [];

  const totalPurchaseAmount = purchaseList.reduce(
    (acc, curr) => acc + (Number(curr.totalAmount) || 0),
    0
  );

  const totalSalePcs = saleList.reduce(
    (acc, curr) => acc + (Number(curr.quantity) || 0),
    0
  );
  const totalSaleWeight = saleQty;
  const totalSaleAmount = saleList.reduce(
    (acc, curr) => acc + (Number(curr.totalAmount) || 0),
    0
  );

  const totalWastageAmount = wastageList.reduce(
    (acc, curr) =>
      acc + (Number(curr.totalAmount) || (Number(curr.quantity) || 0) * (Number(curr.price) || 0)),
    0
  );

  return (
    <Layout reload={fetchStockData} hideDatePicker={true}>
      {loading && <Loading />}

      <div className="flex flex-col gap-5 max-w-7xl mx-auto pb-10">
        {/* Top Header & Actions */}
        <div className="flex flex-wrap items-center justify-between gap-3 bg-white p-4 rounded-xl border border-gray-200 shadow-xs">
          <div>
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="text-xl font-black text-slate-800 flex items-center gap-2">
                <span className="text-2xl">🧵</span>
                <span>Stock Management</span>
              </h1>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                📅 From 01/09/2026 onwards
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Showing active Purchases, Sales &amp; Wastage records from 01/09/2026 onwards
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={fetchStockData}
              disabled={loading}
              className="bg-gray-100 hover:bg-gray-200 text-slate-800 text-xs font-bold px-3 py-2 rounded-lg border border-gray-300 transition active:scale-95 flex items-center gap-1.5 shadow-2xs cursor-pointer"
            >
              <span>🔄</span>
              <span>Refresh Stock</span>
            </button>
            <button
              onClick={() => window.print()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-2 rounded-lg transition active:scale-95 flex items-center gap-1.5 shadow-sm cursor-pointer"
            >
              <span>🖨️</span>
              <span>Print Report</span>
            </button>
          </div>
        </div>

        {/* 5-Column Master Summary Cards (Clickable Tabs) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3.5">
          {/* Card 1: Total Purchase Qty (Tab) */}
          <div
            onClick={() => setActiveTab("purchase")}
            className={`border-2 rounded-xl p-4 shadow-xs flex flex-col justify-between cursor-pointer transition-all duration-150 select-none ${
              activeTab === "purchase"
                ? "bg-amber-50/80 border-amber-500 ring-3 ring-amber-400/50 shadow-md scale-[1.01]"
                : "bg-white border-amber-300 hover:border-amber-400 hover:shadow-sm"
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                <span>Total Purchase Qty</span>
                {activeTab === "purchase" && (
                  <span className="bg-amber-400 text-slate-900 text-[9px] px-1.5 py-0.2 rounded font-black uppercase">
                    Active
                  </span>
                )}
              </span>
              <span className="text-lg bg-amber-100 p-1 rounded-md">📦</span>
            </div>
            <div className="mt-3">
              <div className="text-2xl font-black font-mono text-slate-900">
                {purchaseQty.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
            </div>
          </div>

          {/* Card 2: Total Sale Qty (Tab) */}
          <div
            onClick={() => setActiveTab("sale")}
            className={`border-2 rounded-xl p-4 shadow-xs flex flex-col justify-between cursor-pointer transition-all duration-150 select-none ${
              activeTab === "sale"
                ? "bg-blue-50/80 border-blue-600 ring-3 ring-blue-400/50 shadow-md scale-[1.01]"
                : "bg-white border-blue-300 hover:border-blue-400 hover:shadow-sm"
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                <span>Total Sale Qty</span>
                {activeTab === "sale" && (
                  <span className="bg-blue-600 text-white text-[9px] px-1.5 py-0.2 rounded font-black uppercase">
                    Active
                  </span>
                )}
              </span>
              <span className="text-lg bg-blue-100 p-1 rounded-md">🏷️</span>
            </div>
            <div className="mt-3">
              <div className="text-2xl font-black font-mono text-blue-700">
                {saleQty.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
            </div>
          </div>

          {/* Card 3: Total Wastage Qty (Tab) */}
          <div
            onClick={() => setActiveTab("wastage")}
            className={`border-2 rounded-xl p-4 shadow-xs flex flex-col justify-between cursor-pointer transition-all duration-150 select-none ${
              activeTab === "wastage"
                ? "bg-orange-50/80 border-orange-500 ring-3 ring-orange-400/50 shadow-md scale-[1.01]"
                : "bg-white border-orange-300 hover:border-orange-400 hover:shadow-sm"
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                <span>Total Wastage Qty</span>
                {activeTab === "wastage" && (
                  <span className="bg-orange-500 text-white text-[9px] px-1.5 py-0.2 rounded font-black uppercase">
                    Active
                  </span>
                )}
              </span>
              <span className="text-lg bg-orange-100 p-1 rounded-md">🗑️</span>
            </div>
            <div className="mt-3">
              <div className="text-2xl font-black font-mono text-orange-800">
                {wastageQty.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
            </div>
          </div>

          {/* Card 4: Total Balance */}
          <div
            className={
              "border-2 rounded-xl p-4 shadow-xs flex flex-col justify-between " +
              (balanceQty >= 0
                ? "bg-emerald-50/70 border-emerald-500"
                : "bg-rose-50/70 border-rose-500")
            }
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                Total Balance
              </span>
              <span className="text-lg p-1 rounded-md">
                {balanceQty >= 0 ? "⚖️" : "⚠️"}
              </span>
            </div>
            <div className="mt-3">
              <div
                className={
                  "text-2xl font-black font-mono " +
                  (balanceQty >= 0 ? "text-emerald-900" : "text-rose-900")
                }
              >
                {balanceQty.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
            </div>
          </div>

          {/* Card 5: Items Stock (Tab) */}
          <div
            onClick={() => setActiveTab("items")}
            className={`border-2 rounded-xl p-4 shadow-xs flex flex-col justify-between cursor-pointer transition-all duration-150 select-none ${
              activeTab === "items"
                ? "bg-teal-50/90 border-teal-600 ring-3 ring-teal-400/50 shadow-md scale-[1.01]"
                : "bg-white border-teal-300 hover:border-teal-400 hover:shadow-sm"
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                <span>Items Stock</span>
                {activeTab === "items" && (
                  <span className="bg-teal-600 text-white text-[9px] px-1.5 py-0.2 rounded font-black uppercase">
                    Active
                  </span>
                )}
              </span>
              <span className="text-lg bg-teal-100 p-1 rounded-md">📊</span>
            </div>
            <div className="mt-3">
              <div className="text-2xl font-black font-mono text-teal-800">
                {itemsStockList.length}{" "}
                <span className="text-xs font-bold text-teal-600 font-sans">
                  {itemsStockList.length === 1 ? "Item" : "Items"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Buttons Navigation Bar */}
        <div className="flex items-center gap-2 border-b border-gray-300 pb-2 overflow-x-auto">
          <button
            onClick={() => setActiveTab("purchase")}
            className={`px-4 py-2 rounded-lg text-xs font-bold transition flex items-center gap-2 cursor-pointer shadow-2xs ${
              activeTab === "purchase"
                ? "bg-amber-400 text-slate-900 border border-black shadow-xs font-black"
                : "bg-gray-100 hover:bg-gray-200 text-slate-700 border border-gray-300"
            }`}
          >
            <span>📦</span>
            <span>Purchase Orders</span>
            <span className={`text-[11px] px-1.5 py-0.2 rounded-full font-mono ${
              activeTab === "purchase" ? "bg-black text-white" : "bg-gray-200 text-slate-800"
            }`}>
              {purchaseList.length}
            </span>
          </button>

          <button
            onClick={() => setActiveTab("sale")}
            className={`px-4 py-2 rounded-lg text-xs font-bold transition flex items-center gap-2 cursor-pointer shadow-2xs ${
              activeTab === "sale"
                ? "bg-blue-600 text-white border border-black shadow-xs font-black"
                : "bg-gray-100 hover:bg-gray-200 text-slate-700 border border-gray-300"
            }`}
          >
            <span>🏷️</span>
            <span>Sale Orders</span>
            <span className={`text-[11px] px-1.5 py-0.2 rounded-full font-mono ${
              activeTab === "sale" ? "bg-white text-blue-900" : "bg-gray-200 text-slate-800"
            }`}>
              {saleList.length}
            </span>
          </button>

          <button
            onClick={() => setActiveTab("wastage")}
            className={`px-4 py-2 rounded-lg text-xs font-bold transition flex items-center gap-2 cursor-pointer shadow-2xs ${
              activeTab === "wastage"
                ? "bg-orange-500 text-white border border-black shadow-xs font-black"
                : "bg-gray-100 hover:bg-gray-200 text-slate-700 border border-gray-300"
            }`}
          >
            <span>🗑️</span>
            <span>Wastage Records</span>
            <span className={`text-[11px] px-1.5 py-0.2 rounded-full font-mono ${
              activeTab === "wastage" ? "bg-white text-orange-950" : "bg-gray-200 text-slate-800"
            }`}>
              {wastageList.length}
            </span>
          </button>

          <button
            onClick={() => setActiveTab("items")}
            className={`px-4 py-2 rounded-lg text-xs font-bold transition flex items-center gap-2 cursor-pointer shadow-2xs ${
              activeTab === "items"
                ? "bg-emerald-600 text-white border border-black shadow-xs font-black"
                : "bg-gray-100 hover:bg-gray-200 text-slate-700 border border-gray-300"
            }`}
          >
            <span>📊</span>
            <span>Items Stock</span>
            <span className={`text-[11px] px-1.5 py-0.2 rounded-full font-mono ${
              activeTab === "items" ? "bg-white text-emerald-950" : "bg-gray-200 text-slate-800"
            }`}>
              {itemsStockList.length}
            </span>
          </button>
        </div>

        {/* Tab 1: Non Woven Cloth Purchase Orders Table */}
        {activeTab === "purchase" && (
          <div className="bg-white rounded-xl border border-black overflow-hidden shadow-sm animate-in fade-in duration-150">
            <div className="bg-amber-400 border-b border-black px-4 py-2 font-bold text-sm text-slate-900 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span>📦 Non Woven Cloth Purchase Orders</span>
                <span className="bg-white/90 text-slate-900 text-[11px] px-2 py-0.5 rounded font-mono font-bold">
                  {purchaseList.length} Orders
                </span>
              </div>
              <span className="text-xs font-mono font-bold">
                Total Purchase Qty: <strong>{purchaseQty.toFixed(2)} KGS</strong>
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="table-auto border-collapse w-full text-left text-xs">
                <thead>
                  <tr className="bg-amber-50 text-slate-800 font-bold border-b border-black">
                    <th className="border-r border-black px-3 py-2">Date</th>
                    <th className="border-r border-black px-3 py-2">Product</th>
                    <th className="border-r border-black px-3 py-2">Supplier / Desc</th>
                    <th className="border-r border-black px-3 py-2 text-right">Quantity (KGS)</th>
                    <th className="border-r border-black px-3 py-2 text-right">Rate (₹)</th>
                    <th className="px-3 py-2 text-right">Total Amount (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {purchaseList.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="text-center py-6 text-slate-400 italic"
                      >
                        No Non Woven Cloth purchase orders found for this period.
                      </td>
                    </tr>
                  ) : (
                    purchaseList.map((po, idx) => (
                      <tr
                        key={po._id || idx}
                        className="border-b border-gray-200 hover:bg-amber-50/40"
                      >
                        <td className="border-r border-gray-200 px-3 py-2 font-mono">
                          {formatDate(po.date || po.createdAt)}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2 font-bold text-slate-800">
                          {po.product?.productName || "Non Woven Cloth"}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2 text-slate-600">
                          {po.customer?.userName || po.description || "-"}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2 text-right font-mono font-bold text-slate-900">
                          {Number(po.quantity || 0).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2 text-right font-mono text-slate-700">
                          ₹{Number(po.rate || 0).toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono font-bold text-slate-900">
                          ₹{Number(po.totalAmount || 0).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                {purchaseList.length > 0 && (
                  <tfoot>
                    <tr className="bg-amber-100 font-bold border-t-2 border-black text-slate-900">
                      <td colSpan={3} className="border-r border-black px-3 py-2 text-right uppercase tracking-wider font-mono">
                        Total Purchase Summary:
                      </td>
                      <td className="border-r border-black px-3 py-2 text-right font-mono font-black text-slate-900 text-sm">
                        {purchaseQty.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} KGS
                      </td>
                      <td className="border-r border-black px-3 py-2 text-right font-mono text-slate-500">
                        -
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-black text-slate-900 text-sm">
                        ₹{totalPurchaseAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        )}

        {/* Tab 2: Non Woven Cloth / Tag Sale Orders Table */}
        {activeTab === "sale" && (
          <div className="bg-white rounded-xl border border-black overflow-hidden shadow-sm animate-in fade-in duration-150">
            <div className="bg-blue-600 border-b border-black px-4 py-2 font-bold text-sm text-white flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <span>🏷️ Tag / Non Woven Sale Orders</span>
                <span className="bg-white text-blue-900 text-[11px] px-2 py-0.5 rounded font-mono font-bold">
                  {saleList.length} Orders
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs font-mono font-bold">
                  Total Weight Sold: <strong>{totalSaleWeight.toFixed(2)} KGS</strong>
                  {totalSaleAmount > 0 && (
                    <span className="ml-2 opacity-90">(₹{totalSaleAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})</span>
                  )}
                </span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="table-auto border-collapse w-full text-left text-xs">
                <thead>
                  <tr className="bg-blue-50 text-slate-800 font-bold border-b border-black">
                    <th className="border-r border-black px-3 py-2">Date</th>
                    <th className="border-r border-black px-3 py-2">Customer Name</th>
                    <th className="border-r border-black px-3 py-2">Product</th>
                    <th className="border-r border-black px-3 py-2 text-right">Quantity (PCS)</th>
                    <th className="border-r border-black px-3 py-2 text-right bg-blue-100/60 font-black text-blue-900">Weight (KGS)</th>
                    <th className="border-r border-black px-3 py-2 text-right">Rate (₹)</th>
                    <th className="px-3 py-2 text-right">Total Amount (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {saleList.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="text-center py-6 text-slate-400 italic"
                      >
                        No sale orders found for this period.
                      </td>
                    </tr>
                  ) : (
                    saleList.map((ord, idx) => {
                      const qtyPcs = Number(ord.quantity || 0);
                      const w = Number(ord.orderWeight !== undefined ? ord.orderWeight : (ord.challanWeight || ord.weight || 0));
                      return (
                        <tr
                          key={ord._id || idx}
                          className="border-b border-gray-200 hover:bg-blue-50/40 transition"
                        >
                          <td className="border-r border-gray-200 px-3 py-2 font-mono">
                            {formatDate(ord.completedDate || ord.dispatchDate || ord.createdAt || ord.date)}
                          </td>
                          <td className="border-r border-gray-200 px-3 py-2 font-bold text-slate-800">
                            {ord.customer?.userName || ord.customerName || "-"}
                          </td>
                          <td className="border-r border-gray-200 px-3 py-2 text-slate-600">
                            {ord.product?.productName || ord.description || "Non Woven Tag"}
                          </td>
                          <td className="border-r border-gray-200 px-3 py-2 text-right font-mono font-bold text-slate-900">
                            {qtyPcs.toLocaleString(undefined, {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 2,
                            })}
                          </td>
                          <td className="border-r border-gray-200 px-3 py-2 text-right font-mono font-bold text-blue-800 bg-blue-50/50">
                            {w > 0 ? `${w.toFixed(2)} kg` : "-"}
                          </td>
                          <td className="border-r border-gray-200 px-3 py-2 text-right font-mono text-slate-700">
                            ₹{Number(ord.rate || 0).toFixed(2)}
                          </td>
                          <td className="px-3 py-2 text-right font-mono font-bold text-slate-900">
                            ₹{Number(ord.totalAmount || 0).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
                {saleList.length > 0 && (
                  <tfoot>
                    <tr className="bg-blue-100 font-bold border-t-2 border-black text-slate-900">
                      <td colSpan={3} className="border-r border-black px-3 py-2 text-right uppercase tracking-wider font-mono">
                        Total Sale Summary:
                      </td>
                      <td className="border-r border-black px-3 py-2 text-right font-mono font-black text-slate-900">
                        {totalSalePcs.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} PCS
                      </td>
                      <td className="border-r border-black px-3 py-2 text-right font-mono font-black text-blue-900 text-sm bg-blue-200/80">
                        {totalSaleWeight.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg
                      </td>
                      <td className="border-r border-black px-3 py-2 text-right font-mono text-slate-500">
                        -
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-black text-slate-900 text-sm">
                        ₹{totalSaleAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        )}

        {/* Tab 3: Non Woven Cloth Wastage Records Table */}
        {activeTab === "wastage" && (
          <div className="bg-white rounded-xl border border-black overflow-hidden shadow-sm animate-in fade-in duration-150">
            <div className="bg-orange-400 border-b border-black px-4 py-2 font-bold text-sm text-slate-900 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <span>🗑️ Wastage Records</span>
                <span className="bg-white/80 text-orange-950 text-[11px] px-2 py-0.5 rounded font-mono font-bold">
                  {wastageList.length} Entries
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs font-mono font-bold">
                  Total Wastage: <strong>{wastageQty.toFixed(2)} KGS</strong>
                  {totalWastageAmount > 0 && (
                    <span className="ml-2 opacity-90">(₹{totalWastageAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})</span>
                  )}
                </span>
                <button
                  onClick={handleOpenAddWastage}
                  className="bg-white hover:bg-orange-50 text-orange-900 font-bold text-xs px-3 py-1 rounded border border-orange-600 shadow-2xs transition active:scale-95 cursor-pointer flex items-center gap-1"
                >
                  <span>➕</span>
                  <span>Add Wastage</span>
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="table-auto border-collapse w-full text-left text-xs">
                <thead>
                  <tr className="bg-orange-50 text-slate-800 font-bold border-b border-black">
                    <th className="border-r border-black px-3 py-2">Date</th>
                    <th className="border-r border-black px-3 py-2 text-right">Quantity (KGS)</th>
                    <th className="border-r border-black px-3 py-2 text-right">Price / Rate (₹)</th>
                    <th className="border-r border-black px-3 py-2 text-right">Total Amount (₹)</th>
                    <th className="border-r border-black px-3 py-2">Note / Reason</th>
                    <th className="px-3 py-2 text-center w-24">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {wastageList.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="text-center py-6 text-slate-400 italic"
                      >
                        No wastage records found for this period. Click <strong>"+ Add Wastage"</strong> to record new wastage.
                      </td>
                    </tr>
                  ) : (
                    wastageList.map((item, idx) => {
                      const itemQty = Number(item.quantity || 0);
                      const itemPrice = Number(item.price || 0);
                      const itemTotal = Number(item.totalAmount || itemQty * itemPrice);
                      return (
                        <tr
                          key={item._id || idx}
                          className="border-b border-gray-200 hover:bg-orange-50/40 transition"
                        >
                          <td className="border-r border-gray-200 px-3 py-2 font-mono">
                            {formatDate(item.date || item.createdAt)}
                          </td>
                          <td className="border-r border-gray-200 px-3 py-2 text-right font-mono font-bold text-orange-900">
                            {itemQty.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </td>
                          <td className="border-r border-gray-200 px-3 py-2 text-right font-mono text-slate-700">
                            ₹{itemPrice.toFixed(2)}
                          </td>
                          <td className="border-r border-gray-200 px-3 py-2 text-right font-mono font-bold text-slate-900">
                            ₹{itemTotal.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </td>
                          <td className="border-r border-gray-200 px-3 py-2 text-slate-600">
                            {item.description || "-"}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              <button
                                onClick={() => handleOpenEditWastage(item)}
                                className="text-xs bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300 px-2 py-0.5 rounded font-bold transition active:scale-95 cursor-pointer shadow-2xs"
                                title="Edit record"
                              >
                                ✏️ Edit
                              </button>
                              <button
                                onClick={() => handleDeleteWastage(item._id)}
                                className="text-xs bg-rose-100 hover:bg-rose-200 text-rose-900 border border-rose-300 px-2 py-0.5 rounded font-bold transition active:scale-95 cursor-pointer shadow-2xs"
                                title="Delete record"
                              >
                                🗑️ Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
                {wastageList.length > 0 && (
                  <tfoot>
                    <tr className="bg-orange-100 font-bold border-t-2 border-black text-slate-900">
                      <td className="border-r border-black px-3 py-2 text-right uppercase tracking-wider font-mono">
                        Total Wastage Summary:
                      </td>
                      <td className="border-r border-black px-3 py-2 text-right font-mono font-black text-orange-950 text-sm">
                        {wastageQty.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} KGS
                      </td>
                      <td className="border-r border-black px-3 py-2 text-right font-mono text-slate-500">
                        -
                      </td>
                      <td className="border-r border-black px-3 py-2 text-right font-mono font-black text-slate-900 text-sm">
                        ₹{totalWastageAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td colSpan={2} className="px-3 py-2"></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        )}

        {/* Tab 4: Items Stock Summary Table (Matching Hand-Drawn Diagram) */}
        {activeTab === "items" && (
          <div className="flex flex-col gap-6">
            <div className="bg-white rounded-xl border-2 border-emerald-600 shadow-sm overflow-hidden">
              {/* Table Header / Action bar */}
              <div className="bg-emerald-600 text-white px-4 py-3 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-xl">📊</span>
                  <span className="font-bold text-sm sm:text-base tracking-wide">
                    Items Stock Summary
                  </span>
                  <span className="text-xs bg-emerald-800 text-white px-2 py-0.5 rounded-full font-mono">
                    {itemsStockList.length} items
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleOpenAddItemWastage}
                    className="bg-white hover:bg-emerald-50 text-emerald-900 text-xs font-bold px-3 py-1.5 rounded-lg border border-emerald-300 transition active:scale-95 flex items-center gap-1 shadow-2xs cursor-pointer"
                  >
                    <span>➕</span>
                    <span>Add Item Wastage</span>
                  </button>
                </div>
              </div>

              {/* Items Stock Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-emerald-50 border-b-2 border-emerald-600 text-slate-800 font-bold uppercase text-[11px] tracking-wider font-mono">
                      <th className="border-r border-gray-300 px-4 py-3">Items</th>
                      <th className="border-r border-gray-300 px-4 py-3 text-right">Pur.</th>
                      <th className="border-r border-gray-300 px-4 py-3 text-right">Sale</th>
                      <th className="border-r border-gray-300 px-4 py-3 text-right">Wastege.</th>
                      <th className="border-r border-gray-300 px-4 py-3 text-right">Bal.</th>
                      <th className="px-4 py-3 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {itemsStockList.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-4 py-8 text-center text-slate-400 italic text-sm"
                        >
                          No items found for the selected date range.
                        </td>
                      </tr>
                    ) : (
                      itemsStockList.map((row, idx) => {
                        const pur = Number(row.pur || 0);
                        const sale = Number(row.sale || 0);
                        const wastage = Number(row.wastage || 0);
                        const bal = Number(row.bal || 0);
                        return (
                          <tr
                            key={row.item || idx}
                            className="border-b border-gray-200 hover:bg-emerald-50/40 transition"
                          >
                            <td className="border-r border-gray-200 px-4 py-2.5 font-bold text-slate-800">
                              {row.item}
                            </td>
                            <td className="border-r border-gray-200 px-4 py-2.5 text-right font-mono font-bold text-amber-900">
                              {pur.toLocaleString()}
                            </td>
                            <td className="border-r border-gray-200 px-4 py-2.5 text-right font-mono font-bold text-blue-900">
                              {sale.toLocaleString()}
                            </td>
                            <td className="border-r border-gray-200 px-4 py-2.5 text-right font-mono font-bold text-orange-900">
                              {wastage.toLocaleString()}
                            </td>
                            <td
                              className={`border-r border-gray-200 px-4 py-2.5 text-right font-mono font-black ${
                                bal < 0
                                  ? "text-rose-600"
                                  : bal > 0
                                  ? "text-emerald-700"
                                  : "text-slate-600"
                              }`}
                            >
                              {bal.toLocaleString()}
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              <button
                                onClick={() => handleOpenAddItemWastageForProduct(row.item)}
                                className="text-xs bg-orange-100 hover:bg-orange-200 text-orange-900 border border-orange-300 px-2.5 py-1 rounded-md font-bold transition active:scale-95 cursor-pointer shadow-2xs"
                                title={`Add Wastage for ${row.item}`}
                              >
                                ➕ Wastage
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                  {itemsStockList.length > 0 && (
                    <tfoot>
                      <tr className="bg-emerald-100 font-bold border-t-2 border-black text-slate-900">
                        <td className="border-r border-black px-4 py-2.5 text-right uppercase tracking-wider font-mono">
                          Total Summary:
                        </td>
                        <td className="border-r border-black px-4 py-2.5 text-right font-mono font-black text-amber-950 text-sm">
                          {itemsStockList.reduce((sum, r) => sum + Number(r.pur || 0), 0).toLocaleString()}
                        </td>
                        <td className="border-r border-black px-4 py-2.5 text-right font-mono font-black text-blue-950 text-sm">
                          {itemsStockList.reduce((sum, r) => sum + Number(r.sale || 0), 0).toLocaleString()}
                        </td>
                        <td className="border-r border-black px-4 py-2.5 text-right font-mono font-black text-orange-950 text-sm">
                          {itemsStockList.reduce((sum, r) => sum + Number(r.wastage || 0), 0).toLocaleString()}
                        </td>
                        <td className="border-r border-black px-4 py-2.5 text-right font-mono font-black text-slate-950 text-sm">
                          {itemsStockList.reduce((sum, r) => sum + Number(r.bal || 0), 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-2.5"></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>

            {/* Item Wastage Detailed Records Sub-Table */}
            {itemWastageList.length > 0 && (
              <div className="bg-white rounded-xl border border-orange-300 shadow-xs overflow-hidden">
                <div className="bg-orange-500 text-white px-4 py-2.5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span>🗑️</span>
                    <span className="font-bold text-sm">Item Wastage Log</span>
                    <span className="text-xs bg-orange-700 text-white px-2 py-0.5 rounded-full font-mono">
                      {itemWastageList.length} records
                    </span>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="bg-orange-50 border-b border-orange-200 text-slate-700 font-bold uppercase text-[10px] tracking-wider font-mono">
                        <th className="border-r border-gray-200 px-3 py-2">Date</th>
                        <th className="border-r border-gray-200 px-3 py-2">Item Name</th>
                        <th className="border-r border-gray-200 px-3 py-2 text-right">Quantity</th>
                        <th className="border-r border-gray-200 px-3 py-2 text-right">Rate (₹)</th>
                        <th className="border-r border-gray-200 px-3 py-2 text-right">Total Amount (₹)</th>
                        <th className="border-r border-gray-200 px-3 py-2">Note</th>
                        <th className="px-3 py-2 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {itemWastageList.map((item, idx) => {
                        const itemQty = Number(item.quantity || 0);
                        const itemPrice = Number(item.price || 0);
                        const itemTotal = Number(item.totalAmount || itemQty * itemPrice);
                        return (
                          <tr
                            key={item._id || idx}
                            className="border-b border-gray-200 hover:bg-orange-50/40 transition"
                          >
                            <td className="border-r border-gray-200 px-3 py-2 font-mono">
                              {formatDate(item.date || item.createdAt)}
                            </td>
                            <td className="border-r border-gray-200 px-3 py-2 font-bold text-slate-800">
                              {item.productName}
                            </td>
                            <td className="border-r border-gray-200 px-3 py-2 text-right font-mono font-bold text-orange-900">
                              {itemQty.toLocaleString()}
                            </td>
                            <td className="border-r border-gray-200 px-3 py-2 text-right font-mono text-slate-700">
                              ₹{itemPrice.toFixed(2)}
                            </td>
                            <td className="border-r border-gray-200 px-3 py-2 text-right font-mono font-bold text-slate-900">
                              ₹{itemTotal.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </td>
                            <td className="border-r border-gray-200 px-3 py-2 text-slate-600">
                              {item.description || "-"}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <div className="flex items-center justify-center gap-1.5">
                                <button
                                  onClick={() => handleOpenEditItemWastage(item)}
                                  className="text-xs bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300 px-2 py-0.5 rounded font-bold transition active:scale-95 cursor-pointer shadow-2xs"
                                  title="Edit record"
                                >
                                  ✏️ Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteItemWastage(item._id)}
                                  className="text-xs bg-rose-100 hover:bg-rose-200 text-rose-900 border border-rose-300 px-2 py-0.5 rounded font-bold transition active:scale-95 cursor-pointer shadow-2xs"
                                  title="Delete record"
                                >
                                  🗑️ Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Non-Woven Cloth Wastage Add / Edit Modal */}
      {showWastageModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-300 w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-150">
            {/* Modal Header */}
            <div className="bg-orange-500 text-white px-5 py-3.5 flex items-center justify-between font-bold">
              <span className="flex items-center gap-2">
                <span>{editingWastageId ? "✏️" : "➕"}</span>
                <span>{editingWastageId ? "Edit Wastage Record" : "Add Wastage Record"}</span>
              </span>
              <button
                onClick={() => setShowWastageModal(false)}
                className="text-white hover:bg-orange-600 rounded-lg p-1 text-lg leading-none cursor-pointer"
              >
                ✕
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSubmitWastage} className="p-5 flex flex-col gap-4">
              {/* Date */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  required
                  value={wastageForm.date}
                  onChange={(e) =>
                    setWastageForm({ ...wastageForm, date: e.target.value })
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500 font-mono"
                />
              </div>

              {/* Quantity & Price Row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Quantity (KGS) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="any"
                    min="0.01"
                    required
                    placeholder="e.g. 50"
                    value={wastageForm.quantity}
                    onChange={(e) =>
                      setWastageForm({ ...wastageForm, quantity: e.target.value })
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500 font-mono font-bold"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Price / Rate (₹)
                  </label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    placeholder="e.g. 150"
                    value={wastageForm.price}
                    onChange={(e) =>
                      setWastageForm({ ...wastageForm, price: e.target.value })
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500 font-mono"
                  />
                </div>
              </div>

              {/* Calculated Total Preview */}
              {Number(wastageForm.quantity) > 0 && Number(wastageForm.price) > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-2.5 text-xs text-orange-950 flex justify-between items-center font-mono">
                  <span>Estimated Total Amount:</span>
                  <strong className="text-sm">
                    ₹{(Number(wastageForm.quantity) * Number(wastageForm.price)).toFixed(2)}
                  </strong>
                </div>
              )}

              {/* Description / Reason */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Note / Reason
                </label>
                <input
                  type="text"
                  placeholder="e.g. Cutting defect / machine scrap"
                  value={wastageForm.description}
                  onChange={(e) =>
                    setWastageForm({ ...wastageForm, description: e.target.value })
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowWastageModal(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-gray-100 rounded-lg transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingWastage}
                  className="bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold px-5 py-2 rounded-lg transition active:scale-95 shadow-sm cursor-pointer disabled:opacity-50"
                >
                  {submittingWastage ? "Saving..." : editingWastageId ? "Update Record" : "Save Record"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Item Wastage Add / Edit Modal */}
      {showItemWastageModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-300 w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-150">
            {/* Modal Header */}
            <div className="bg-emerald-600 text-white px-5 py-3.5 flex items-center justify-between font-bold">
              <span className="flex items-center gap-2">
                <span>{editingItemWastageId ? "✏️" : "➕"}</span>
                <span>{editingItemWastageId ? "Edit Item Wastage Record" : "Add Item Wastage Record"}</span>
              </span>
              <button
                onClick={() => setShowItemWastageModal(false)}
                className="text-white hover:bg-emerald-700 rounded-lg p-1 text-lg leading-none cursor-pointer"
              >
                ✕
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSubmitItemWastage} className="p-5 flex flex-col gap-4">
              {/* Product / Item Name */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Item / Product Name <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={itemWastageForm.productName}
                  onChange={(e) =>
                    setItemWastageForm({ ...itemWastageForm, productName: e.target.value })
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500 font-bold text-slate-800"
                >
                  <option value="">-- Select Item --</option>
                  {itemsStockList.map((it) => (
                    <option key={it.item} value={it.item}>
                      {it.item}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  required
                  value={itemWastageForm.date}
                  onChange={(e) =>
                    setItemWastageForm({ ...itemWastageForm, date: e.target.value })
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500 font-mono"
                />
              </div>

              {/* Quantity & Price Row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Quantity <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="any"
                    min="0.01"
                    required
                    placeholder="e.g. 5"
                    value={itemWastageForm.quantity}
                    onChange={(e) =>
                      setItemWastageForm({ ...itemWastageForm, quantity: e.target.value })
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500 font-mono font-bold"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Price / Rate (₹)
                  </label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    placeholder="e.g. 50"
                    value={itemWastageForm.price}
                    onChange={(e) =>
                      setItemWastageForm({ ...itemWastageForm, price: e.target.value })
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500 font-mono"
                  />
                </div>
              </div>

              {/* Calculated Total Preview */}
              {Number(itemWastageForm.quantity) > 0 && Number(itemWastageForm.price) > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 text-xs text-emerald-950 flex justify-between items-center font-mono">
                  <span>Estimated Total Amount:</span>
                  <strong className="text-sm">
                    ₹{(Number(itemWastageForm.quantity) * Number(itemWastageForm.price)).toFixed(2)}
                  </strong>
                </div>
              )}

              {/* Description / Reason */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Note / Reason
                </label>
                <input
                  type="text"
                  placeholder="e.g. Damaged / defect item"
                  value={itemWastageForm.description}
                  onChange={(e) =>
                    setItemWastageForm({ ...itemWastageForm, description: e.target.value })
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowItemWastageModal(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-gray-100 rounded-lg transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingItemWastage}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-5 py-2 rounded-lg transition active:scale-95 shadow-sm cursor-pointer disabled:opacity-50"
                >
                  {submittingItemWastage ? "Saving..." : editingItemWastageId ? "Update Record" : "Save Record"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default NonWovenStockPage;


